import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Spin,
  Card,
  Input,
  Button,
  Space,
  message,
  Switch,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  UserOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import httpInjectorService from "../../services/http-injector.service";
import { NotificationContext } from "../../NotificationProvider";
import { useRefresh } from "../../RefreshContext";
import { Col, Row } from "reactstrap";

const UserList = ({ activeTab }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const { openNotification } = useContext(NotificationContext);
  const { refreshKey, triggerRefresh } = useRefresh();
  const [pageSize, setPageSize] = useState(10);

  const formatSortCode = (code) => {
    return code?.match(/.{1,2}/g)?.join("-") || "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear(); // full 4-digit year
    return `${day}-${month}-${year}`;
  };

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatNINumber = (value) => {
    if (!value) return "";
    const clean = value.toUpperCase().replace(/\s+/g, "");

    const pairs = clean.match(/.{1,2}/g);

    return pairs ? pairs.join(" ") : value;
  };

  const startEditing = (record) => {
    setEditingId(record.id);
    setEditedRow({ ...record });
  };

  const getuserDetails = async () => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const res = await httpInjectorService.getUsersheetDetails();
      if (res.status === "SUCCESS") {
        const sorted = res.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        setData(sorted);
      } else {
        setData([]);
        message.warning("No employees found.");
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to fetch employee list.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = data.filter((emp) => {
    const term = searchTerm.toLowerCase();
    const joiningDate = formatDate(emp.created_at).toLowerCase();

    return (
      emp.first_name?.toLowerCase().includes(term) ||
      emp.last_name?.toLowerCase().includes(term)
    );
  });

  const handleDownloadExcel = async () => {
    if (!data || data.length === 0) {
      message.warning("No data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Employees");

    worksheet.columns = [
      { header: "S.NO", key: "sno", width: 5 },
      { header: "FIRST NAME", key: "first_name", width: 15 },
      { header: "LAST NAME", key: "last_name", width: 15 },
      { header: "PASSPORT NUMBER", key: "passport_number", width: 20 },
      {
        header: "NATIONAL INSURANCE NUMBER",
        key: "national_insurance_number",
        width: 25,
      },
      {header:"VERIFICATION REQUIRED", key:"verification_required", width:15},
    ];

    data.forEach((row, index) => {
      worksheet.addRow({
        sno: index + 1,
        first_name: capitalizeWords(row.first_name),
        last_name: capitalizeWords(row.last_name),
        passport_number: row.passport_number,
        national_insurance_number: formatNINumber(
          row.national_insurance_number,
        ),
        verification_required:row.verification_required
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "employee_list.xlsx");
  };

  const saveChanges = async () => {
    const orig = data.find((r) => r.id === editingId);
    if (!orig) return;
    const changedFields = {};

    Object.keys(editedRow).forEach((key) => {
      if (editedRow[key] !== orig[key]) {
        changedFields[key] = editedRow[key];
      }
    });

    const payload = {
      id: editingId,
      file_id: orig.file_id,
      updated_fields: changedFields,
    };
    if (Object.keys(payload).length === 1) {
      openNotification("info", "No changes detected");
      return cancelEditing();
    }
    try {
      const res = await httpInjectorService.updateusersheetdata(payload);
      if (res.status === "SUCCESS") {
        openNotification("success", res.message);
        cancelEditing();
        getuserDetails();
      } else openNotification("error", res.message);
    } catch (e) {
      console.error(e);
      openNotification("error", "Failed to update employee");
    }
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditedRow({});
  };

  const handleChange = (e, field) => {
    const value = e.target ? e.target.value : e;

    setEditedRow((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (activeTab === "1") {
      getuserDetails();
    }
  }, [activeTab]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "index",
      width: 60,
      render: (_, __, idx) => idx + 1,
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "First Name",
      dataIndex: "first_name",
      sorter: (a, b) => a.first_name.localeCompare(b.first_name),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      render: (_, record) =>
        editingId === record.id ? (
          <Input
            value={editedRow.first_name}
            onChange={(e) => handleChange(e, "first_name")}
          />
        ) : (
          <Space size={14}>
            <Avatar size="small" icon={<UserOutlined />} />
            <span>{capitalizeWords(record.first_name)}</span>
          </Space>
        ),
    },
    {
      title: "Last Name",
      dataIndex: "last_name",
      sorter: (a, b) => a.last_name.localeCompare(b.last_name),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      render: (_, record) =>
        editingId === record.id ? (
          <Input
            value={editedRow.last_name}
            onChange={(e) => handleChange(e, "last_name")}
          />
        ) : (
          <Space size={14}>
            <span>{capitalizeWords(record.last_name)}</span>
          </Space>
        ),
    },
    {
      title: "Passport Number",
      dataIndex: "passport_number",
      sorter: (a, b) => a.passport_number.localeCompare(b.passport_number),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      render: (_, record) =>
        editingId === record.id ? (
          <Input
            value={editedRow.passport_number}
            onChange={(e) => handleChange(e, "passport_number")}
          />
        ) : (
          record.passport_number
        ),
    },
    {
      title: "National Insurance Number",
      dataIndex: "national_insurance_number",
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      render: (_, record) =>
        editingId === record.id ? (
          <Input
            value={editedRow.national_insurance_number}
            onChange={(e) => handleChange(e, "national_insurance_number")}
          />
        ) : (
          formatNINumber(record.national_insurance_number)
        ),
    },
 {
  title: "Verification Required",
  dataIndex: "verification_required",
  key: "verification_required",
  onHeaderCell: () => ({
    style: { backgroundColor: "#5167c1", color: "black" },
  }),
  render: (_, record) => {
    const currentValue =
      editingId === record.id
        ? editedRow.verification_required
        : record.verification_required;

    return (
      <Switch
        checked={String(currentValue).toLowerCase() === "yes"}
        checkedChildren="Yes"
        unCheckedChildren="No"
        disabled={editingId !== record.id}
        onChange={(checked) => {
          handleChange(
            checked ? "Yes" : "No",
            "verification_required"
          );
        }}
      />
    );
  },
},
    {
      title: "Actions",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) =>
        editingId === record.id ? (
          <Space>
            <Button type="primary" onClick={saveChanges}>
              Save
            </Button>
            <Button onClick={cancelEditing}>Cancel</Button>
          </Space>
        ) : (
          <Tooltip title="Edit employee">
            <Button
              icon={<EditOutlined />}
              onClick={() => startEditing(record)}
            />
          </Tooltip>
        ),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      width: 120,
    },
  ];

  return (
    <div>
      <Card bordered className="mt-2">
        <Row
          className="align-items-center mb-3"
          style={{ gap: 16, flexWrap: "wrap" }}
        >
          <Col className="col-sm-2">
            <select
              className="form-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </Col>

          <Col className="col-sm-3">
            <div className="position-relative">
              <i
                className="bx bx-search-alt"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                  fontSize: "18px",
                  color: "#aaa",
                }}
              ></i>
              <input
                onChange={(e) => setSearchTerm(e.target.value)}
                id="search-bar-0"
                type="text"
                className="form-control ps-5"
                placeholder={`${filtered.length} record${
                  filtered.length !== 1 ? "s" : ""
                }...`}
                value={searchTerm || ""}
                style={{ height: "38px" }}
              />
            </div>
          </Col>

          <Col className="ms-auto col-sm-2 text-end">
            <button
              style={{ backgroundColor: "#5167c1", color: "black" }}
              className="btn"
              onClick={handleDownloadExcel}
            >
              <i className="bx bx-download me-1"></i>
              Download
            </button>
          </Col>
        </Row>

        <Spin spinning={loading}>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflowY: "scroll",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            className="hide-scrollbar"
          >
            <Table
              key={searchTerm}
              columns={columns}
              dataSource={filtered}
              pagination={filtered.length > pageSize ? { pageSize } : false}
              rowKey="id"
              scroll={{ x: "max-content" }}
            />
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default UserList;
